import { createColumnHelper } from '@tanstack/react-table'
import type { ParseKeys } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { byDecimal } from '@/components/data/byDecimal'
import { ColumnsMenu } from '@/components/data/ColumnsMenu'
import { RecordId, RowValue } from '@/components/data/DataTable'
import { FacetFilter } from '@/components/data/FacetFilter'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { toHiddenColumns, toVisibilityState, useViewState } from '@/components/data/useViewState'
import { CountryText } from '@/components/ui/CountryText'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { ClientOverviewRow } from '@/types/domain'

/* The workbook's summary sheet, on the shared dense-table layer (UX-DR-4):
 * sortable, remembered columns, a status facet and a balance facet, a card per
 * row below the table breakpoint. Money columns exist only when the server
 * sent money; `showMoney` is the page's reading of `moneyUnavailable`, never a
 * role check of its own (AD-21). */

const column = createColumnHelper<ClientOverviewRow>()

/** One entry of the columns menu, before it is translated. */
interface MenuColumn {
  id: string
  labelKey: ParseKeys
  locked?: boolean
}

const DIRECTORY_COLUMNS: readonly MenuColumn[] = [
  { id: 'name', labelKey: 'clients.columns.client', locked: true },
  { id: 'salesContact', labelKey: 'clients.columns.salesContact' },
  { id: 'managers', labelKey: 'clients.columns.managers' },
  { id: 'cases', labelKey: 'clients.columns.cases' },
  { id: 'weightKg', labelKey: 'clients.columns.weight' },
]
const MONEY_COLUMNS: readonly MenuColumn[] = [
  { id: 'billed', labelKey: 'clients.columns.billed' },
  { id: 'received', labelKey: 'clients.columns.received' },
  // S-22f: money paid back to the client in the year.
  { id: 'refunded', labelKey: 'clients.columns.refunded' },
  { id: 'balance', labelKey: 'clients.columns.balance' },
  { id: 'cost', labelKey: 'clients.columns.cost' },
]
const STATUS_COLUMN: MenuColumn = { id: 'status', labelKey: 'clients.columns.status' }

const LOCKED: readonly string[] = ['name']
const NO_FILTER: readonly string[] = []

export function ClientsOverviewTable({
  rows: all,
  showMoney,
  onOpen,
}: {
  rows: ClientOverviewRow[]
  showMoney: boolean
  onOpen: (row: ClientOverviewRow) => void
}) {
  const { t } = useTranslation()
  const [viewState, controls] = useViewState('clients')

  const statusFilter = viewState.filters.status ?? NO_FILTER
  const balanceFilter = viewState.filters.balance ?? NO_FILTER

  /* Narrowing only (AD-5): the server already chose the rows. "With balance"
     keeps a row whose balance is not zero — a negative one is an overpayment,
     which is money to give back and very much a balance. */
  const rows = useMemo(
    () =>
      all.filter((row) => {
        if (statusFilter.length > 0 && !statusFilter.includes(row.isActive ? 'active' : 'inactive')) {
          return false
        }
        if (balanceFilter.length > 0 && !(row.money && Number(row.money.balance.amount) !== 0)) {
          return false
        }
        return true
      }),
    [all, statusFilter, balanceFilter],
  )

  const menu = useMemo(
    () =>
      [...DIRECTORY_COLUMNS, ...(showMoney ? MONEY_COLUMNS : []), STATUS_COLUMN].map((entry) => ({
        id: entry.id,
        label: t(entry.labelKey),
        locked: entry.locked,
      })),
    [showMoney, t],
  )

  const columnVisibility = useMemo(
    () => toVisibilityState(viewState.hiddenColumns.filter((id) => !LOCKED.includes(id))),
    [viewState.hiddenColumns],
  )

  const money = (
    id: 'billed' | 'received' | 'refunded' | 'balance' | 'cost',
    labelKey: ParseKeys,
  ) =>
    column.accessor((row) => row.money?.[id].amount ?? '', {
      id,
      header: t(labelKey),
      size: 128,
      meta: { numeric: true },
      sortingFn: byDecimal<ClientOverviewRow>((row) => row.money?.[id].amount ?? '0'),
      cell: (info) => {
        const value = info.getValue()
        if (value === '') return t('common.empty')
        const negative = id === 'balance' && Number(value) < 0
        return (
          <span className={cn(id === 'balance' && 'font-semibold', negative && 'text-negative')}>
            {formatNumber(value, 2)}
          </span>
        )
      },
    })

  const columns = useMemo(
    () => [
      /* The registry number is the client's identifier now — every client has
         one, from one company-wide series — so it is the id under the name.
         The optional hand-typed code stays beside it for the readers who
         learned clients by it. */
      column.accessor('name', {
        header: t('clients.columns.client'),
        cell: (info) => (
          <RowValue
            primary={info.getValue()}
            secondary={
              <>
                <RecordId>{info.row.original.number}</RecordId>
                {info.row.original.code ? <span> · {info.row.original.code}</span> : null}
              </>
            }
          />
        ),
      }),
      /* The client's own salesperson — NOT the managers of its cases, which is
         the next column and a different set of people. */
      column.accessor((row) => row.ownerName ?? '', {
        id: 'salesContact',
        header: t('clients.columns.salesContact'),
        cell: (info) => info.getValue() || t('common.empty'),
      }),
      column.accessor((row) => row.managers.map((m) => m.name).join(', '), {
        id: 'managers',
        header: t('clients.columns.managers'),
        cell: (info) => info.getValue() || t('common.empty'),
      }),
      column.accessor('cases', {
        header: t('clients.columns.cases'),
        size: 88,
        meta: { numeric: true },
        cell: (info) => formatNumber(info.getValue()),
      }),
      column.accessor((row) => row.weightKg ?? '', {
        id: 'weightKg',
        header: t('clients.columns.weight'),
        size: 120,
        meta: { numeric: true },
        sortingFn: byDecimal<ClientOverviewRow>((row) => row.weightKg ?? '0'),
        cell: (info) => (info.getValue() === '' ? t('common.empty') : formatNumber(info.getValue(), 0)),
      }),
      ...(showMoney
        ? [
            money('billed', 'clients.columns.billed'),
            money('received', 'clients.columns.received'),
            money('refunded', 'clients.columns.refunded'),
            money('balance', 'clients.columns.balance'),
            money('cost', 'clients.columns.cost'),
          ]
        : []),
      column.accessor('isActive', {
        id: 'status',
        header: t('clients.columns.status'),
        size: 104,
        cell: (info) => (
          <StatusPill tone={info.getValue() ? 'positive' : 'neutral'}>
            {info.getValue() ? t('common.form.active') : t('common.form.inactive')}
          </StatusPill>
        ),
      }),
    ],
    // `money` closes over t only; listing it would rebuild columns per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, showMoney],
  )

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('common.form.active') },
      { value: 'inactive', label: t('common.form.inactive') },
    ],
    [t],
  )
  const balanceOptions = useMemo(
    () => [{ value: 'with', label: t('clients.filters.withBalance') }],
    [t],
  )

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5 px-3.5 pb-2.5">
        <FacetFilter
          label={t('common.filters.status')}
          options={statusOptions}
          selected={statusFilter}
          onChange={(values) => controls.setFilter('status', values)}
        />
        {showMoney ? (
          <FacetFilter
            label={t('clients.filters.withBalance')}
            options={balanceOptions}
            selected={balanceFilter}
            onChange={(values) => controls.setFilter('balance', values)}
          />
        ) : null}
        <ColumnsMenu
          columns={menu}
          hidden={viewState.hiddenColumns}
          onToggle={controls.toggleColumn}
          onReset={() => controls.setHiddenColumns([])}
        />
      </div>

      <RecordList
        label={t('clients.table.label')}
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        onRowClick={onOpen}
        filtered={rows.length === 0 && all.length > 0}
        onClearFilters={controls.clearFilters}
        renderCard={(row) => (
          <RecordCard
            id={row.number}
            primary={row.name}
            // The country READ, not printed raw: the client sheet stores an
            // alpha-2 code, which on a card would say "TM" for Turkmenistan.
            secondary={
              row.managers.map((m) => m.name).join(', ') ||
              (row.country.trim() ? <CountryText value={row.country} /> : undefined)
            }
            status={
              <StatusPill tone={row.isActive ? 'positive' : 'neutral'}>
                {row.isActive ? t('common.form.active') : t('common.form.inactive')}
              </StatusPill>
            }
            value={row.money ? formatNumber(row.money.balance.amount, 2) : undefined}
            valueLabel={row.money ? t('clients.columns.balance') : undefined}
          />
        )}
        sorting={viewState.sorting}
        onSortingChange={controls.setSorting}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={(next) => controls.setHiddenColumns(toHiddenColumns(next))}
      />
    </>
  )
}
